import type { AccountProfile } from '@sorrem/shared';
import type { UIDebugPageLink } from '@sorrem-client/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { Button, Icon, LinkButton, Separator, TabStrip, type TabDef, type TabStripSizing } from '@sorrem-client/ui/components';
import { SettingsPopover } from '@sorrem-client/ui/features/SettingsPopover';
import { NotificationsPanel } from './components/NotificationsPanel';
import styles from './CharacterHomeHeader.module.scss';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

/** Where the title leads: Character Home, from any page that shows this header. */
const CHARACTER_HOME_PATH = '/character/overview';

interface CharacterHomeHeaderProps<T extends string> {
    /** Resolved account profile. Null until loaded. */
    accountProfile: AccountProfile | null;
    /** Whether the account holds the developer role; adds a Developer role badge and menu. */
    isDeveloper: boolean;
    /** The developer pages; the account menu's Developer Tools link opens the first. Ignored unless `isDeveloper`. */
    debugPages: readonly UIDebugPageLink[];
    /** Whether the account holds the creator role; adds a Creator role badge. */
    isCreator: boolean;
    /** The editors this account may open (creators' and developers'); the account menu's Editors link opens the first. Empty hides it. */
    editorPages: readonly UIDebugPageLink[];
    /** Called with a path when the user clicks the title to go home. */
    onNavigate: (path: string) => void;
    /** Called when the user clicks the Logout option. */
    onLogout: () => void;
    /** The tab strip's tabs: Character Home's own, or the developer pages' on those pages. */
    tabs: TabDef<T>[];
    /** How the tabs share the strip: evenly (Character Home) or each as wide as its label (the developer pages). */
    tabSizing?: TabStripSizing;
    /** The tab being shown, or `null` when none of `tabs` is this page. */
    activeTab: T | null;
    /** Called when the user picks a tab. */
    onTabChange: (tab: T) => void;
    /** Called with the tab under the pointer or focus, and `null` once it leaves; the room highlights that tab's prop. */
    onTabHover?: (tab: T | null) => void;
    /** Whether the tabs show; character creation hides them. */
    areTabsShown: boolean;
    /** Optional extra class applied to the root element. */
    className?: string;
}

/** A single icon-triggered dropdown's open/close state, closing itself on an outside click. */
function useHeaderDropdown(): {
    open: boolean;
    toggle: () => void;
    close: () => void;
    ref: React.RefObject<HTMLDivElement>;
} {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const toggle = useCallback((): void => {
        setOpen((prev) => !prev);
    }, []);

    const close = useCallback((): void => {
        setOpen(false);
    }, []);

    useEffect(() => {
        if (!open) return;

        const handleOutsideClick = (event: MouseEvent): void => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return (): void => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [open]);

    return { open, toggle, close, ref };
}

/**
 * Character Home's header, also worn by the developer pages: the game title
 * (always a link back to Character Home), a tab strip centered on the screen
 * (Character Home's tabs, or the developer pages' on those pages), settings and notifications icon buttons, and a clickable account identity button with a drop-down
 * menu containing the account's role badges (its tier, plus Developer and
 * Creator), a Developer Tools link to the debug pages for developers, an
 * Editors link to the editors for creators and developers, and a logout
 * action.
 */
export function CharacterHomeHeader<T extends string>({
    accountProfile,
    isDeveloper,
    debugPages,
    isCreator,
    editorPages,
    onNavigate,
    onLogout,
    tabs,
    tabSizing,
    activeTab,
    onTabChange,
    onTabHover,
    areTabsShown,
    className,
}: CharacterHomeHeaderProps<T>): JSX.Element {
    const notificationsMenu = useHeaderDropdown();
    const accountMenu = useHeaderDropdown();

    const displayName = accountProfile?.account.displayName ?? null;
    const tierLabel =
        accountProfile?.accountTypeConfig.badgeLabel ??
        accountProfile?.accountTypeConfig.displayName ??
        null;
    const roleLabels = [
        ...(tierLabel !== null ? [tierLabel] : []),
        ...(isDeveloper ? ['Developer'] : []),
        ...(isCreator ? ['Creator'] : []),
    ];

    const developerToolsPath = isDeveloper ? debugPages[0]?.path ?? null : null;
    const editorsPath = editorPages[0]?.path ?? null;

    const handleLogoutClick = useCallback((): void => {
        accountMenu.close();
        onLogout();
    }, [accountMenu, onLogout]);

    return (
        <header className={classnames(styles.characterHomeHeader, className)}>
            <h1 className={styles.title}>
                {/* A native button so the title stays a heading that is also a link home, styled to look like plain title text. */}
                <button type="button" className={styles.titleLink} onClick={() => onNavigate(CHARACTER_HOME_PATH)}>
                    Sorrem
                </button>
            </h1>

            <TabStrip
                tabs={tabs}
                sizing={tabSizing}
                activeTab={activeTab}
                onTabChange={onTabChange}
                onTabHover={onTabHover}
                className={classnames(styles.tabStrip, areTabsShown && styles.visible)}
                {...toTourTargetProps('tabs')}
            />

            <div className={styles.accountArea} {...toTourTargetProps('header-account')}>
                {accountProfile !== null && (
                    <>
                        <SettingsPopover placement="bottom" className={styles.iconMenu} />

                        <div className={styles.iconMenu} ref={notificationsMenu.ref}>
                            <Icon
                                icon="bell"
                                tooltip="Notifications"
                                onClick={notificationsMenu.toggle}
                                aria-expanded={notificationsMenu.open}
                                aria-haspopup="menu"
                            />

                            {notificationsMenu.open && (
                                <div className={styles.dropdownPanel} role="menu">
                                    <NotificationsPanel />
                                </div>
                            )}
                        </div>

                        <div className={styles.accountMenu} ref={accountMenu.ref}>
                            {/* Native button: it reads as the account's name, unlike the project Button, and stays keyboard-reachable. */}
                            <button
                                type="button"
                                className={classnames(styles.accountTrigger, accountMenu.open && styles.accountTriggerOpen)}
                                onClick={accountMenu.toggle}
                                aria-expanded={accountMenu.open}
                                aria-haspopup="menu"
                            >
                                {displayName}
                                <Icon icon="circle-arrow-down" aria-hidden="true" rotate={accountMenu.open ? 180 : 0} />
                            </button>

                            {accountMenu.open && (
                                <div className={styles.dropdownPanel} role="menu">
                                    {roleLabels.length > 0 && (
                                        <div className={styles.roleBadges} aria-label="Account roles">
                                            {roleLabels.map((label) => (
                                                <div key={label} className={styles.roleBadge} role="menuitem" aria-disabled="true">
                                                    {label}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <Separator />

                                    {developerToolsPath !== null && (
                                        <>
                                            {/*
                                              * In a tab of their own, reused by every click. No rel="noopener":
                                              * the new tab copies this one's session, so it opens signed in.
                                              */}
                                            <LinkButton
                                                href={developerToolsPath}
                                                target="sorrem-dev"
                                                variant="secondary"
                                                className={styles.menuButton}
                                                role="menuitem"
                                                onClick={accountMenu.close}
                                            >
                                                Developer Tools
                                            </LinkButton>

                                            <Separator />
                                        </>
                                    )}

                                    {editorsPath !== null && (
                                        <>
                                            {/* In a tab of their own, as Developer Tools above, apart from it. */}
                                            <LinkButton
                                                href={editorsPath}
                                                target="sorrem-editors"
                                                variant="secondary"
                                                className={styles.menuButton}
                                                role="menuitem"
                                                onClick={accountMenu.close}
                                            >
                                                Editors
                                            </LinkButton>

                                            <Separator />
                                        </>
                                    )}

                                    <Button
                                        variant="secondary"
                                        className={styles.logoutButton}
                                        role="menuitem"
                                        onClick={handleLogoutClick}
                                    >
                                        Logout
                                    </Button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </header>
    );
}
